import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TEMPLATES_LIST, TEMPLATES } from "../EventoPublico/templates";
import { defaultLayoutConfig, RSVP_FONT_OPTIONS } from "../../utils/layoutUtils";
import { listarVariantes, criarVariante, atualizarVariante, deletarVariante } from "../../services/templateVariantService";
import AlertComponent from "../../components/AlertComponent";
import MockRsvpForm from "../../components/MockRsvpForm";
import ImageSourceField from "../../components/ImageSourceField";
import { Context } from "../../Reducers/Store";

const MOCK_EVENTO = {
  nome: "Nome do evento",
  descricao: "1 Ano",
  dataDoEvento: "2026-06-15T16:00:00",
  dataLimiteConfirmacao: "2026-04-23T22:00:00",
  localizacao: "Rua das Flores, 123 — São Paulo, SP",
  imagemUrl: null,
  mapaUrl: "https://maps.google.com",
  isActive: true,
};

// ─── Página principal ─────────────────────────────────────────────────────────

export default function LayoutsManager() {
  const [state] = useContext(Context);
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [layoutCfg, setLayoutCfg] = useState(defaultLayoutConfig);
  const [variants, setVariants] = useState([]);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [variantNome, setVariantNome] = useState("");
  const [saving, setSaving] = useState(false);
  const [variantCounts, setVariantCounts] = useState({});
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [variantsCache, setVariantsCache] = useState({});
  const [rsvpPreviewMode, setRsvpPreviewMode] = useState("form");
  const templateRequestRef = useRef(null);

  useEffect(() => {
    if (!state?.profile?.podeCriarLayouts) {
      navigate("/eventos", { replace: true });
    }
  }, [navigate, state?.profile?.podeCriarLayouts]);

  // Carrega contagem de variantes por template ao abrir a lista
  useEffect(() => {
    if (selectedTemplate || Object.keys(variantCounts).length === TEMPLATES_LIST.length) return undefined;

    const controller = new AbortController();
    let cancelled = false;

    async function loadCounts() {
      const counts = {};
      await Promise.allSettled(
        TEMPLATES_LIST.map(async (t) => {
          try {
            const data = await listarVariantes(t.id, { signal: controller.signal });
            counts[t.id] = data.length;
          } catch {
            if (!controller.signal.aborted) {
              counts[t.id] = 0;
            }
          }
        })
      );

      if (!cancelled && !controller.signal.aborted) {
        setVariantCounts((current) => ({ ...current, ...counts }));
      }
    }

    loadCounts();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [selectedTemplate, variantCounts]);

  const openTemplate = async (t) => {
    templateRequestRef.current?.abort();
    const controller = new AbortController();
    templateRequestRef.current = controller;
    const cachedVariants = variantsCache[t.id];

    setSelectedTemplate(t);
    setLayoutCfg(defaultLayoutConfig);
    setSelectedVariantId(null);
    setVariantNome("");
    setVariants(cachedVariants ?? []);
    setVariantsLoading(!cachedVariants);

    try {
      const data = await listarVariantes(t.id, { signal: controller.signal });
      if (controller.signal.aborted) return;

      setVariants(data);
      setVariantsCache((current) => ({ ...current, [t.id]: data }));
      setVariantCounts((current) => ({ ...current, [t.id]: data.length }));
    } catch {
      if (!controller.signal.aborted) {
        setVariants([]);
      }
    } finally {
      if (!controller.signal.aborted) {
        setVariantsLoading(false);
      }
      if (templateRequestRef.current === controller) {
        templateRequestRef.current = null;
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedVariantId) {
        const updated = await atualizarVariante(selectedVariantId, variantNome || undefined, layoutCfg);
        const nextVariants = variants.map(x => x.id === updated.id ? updated : x);
        setVariants(nextVariants);
        setVariantsCache(c => ({ ...c, [selectedTemplate.id]: nextVariants }));
        AlertComponent("Sucesso", "Variante atualizada!", { icon: "success" });
      } else {
        const created = await criarVariante(selectedTemplate.id, variantNome || null, layoutCfg);
        const nextVariants = [...variants, created];
        setVariants(nextVariants);
        setVariantsCache(c => ({ ...c, [selectedTemplate.id]: nextVariants }));
        setSelectedVariantId(created.id);
        setVariantNome(created.nome);
        setVariantCounts(c => ({ ...c, [selectedTemplate.id]: nextVariants.length }));
        AlertComponent("Sucesso", "Variante salva!", { icon: "success" });
      }
    } catch {
      AlertComponent("Erro", "Não foi possível salvar", { icon: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedVariantId || !window.confirm("Excluir esta variante?")) return;
    try {
      await deletarVariante(selectedVariantId);
      const nextVariants = variants.filter(x => x.id !== selectedVariantId);
      setVariants(nextVariants);
      setVariantsCache(c => ({ ...c, [selectedTemplate.id]: nextVariants }));
      setVariantCounts(c => ({ ...c, [selectedTemplate.id]: nextVariants.length }));
      setSelectedVariantId(null);
      setVariantNome("");
      setLayoutCfg(defaultLayoutConfig);
    } catch {
      AlertComponent("Erro", "Não foi possível excluir", { icon: "error" });
    }
  };

  // ─── Tela de lista ───────────────────────────────────────────────────────────

  if (!selectedTemplate) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-slate-800">Layouts</h1>
          <p className="text-sm text-slate-500 mt-1">
            Crie e gerencie variantes visuais para cada template de convite.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {TEMPLATES_LIST.map(t => {
            const hasCount = Object.prototype.hasOwnProperty.call(variantCounts, t.id);
            const count = variantCounts[t.id] ?? 0;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => openTemplate(t)}
                className="bg-white rounded-xl border shadow-sm p-5 text-left hover:shadow-md hover:border-slate-300 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <span className="text-4xl leading-none shrink-0">{t.emoji}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800">{t.label}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{t.desc}</p>
                    <div className="mt-3">
                      {!hasCount ? (
                        <span className="text-xs text-slate-400">Carregando variantes...</span>
                      ) : count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium bg-violet-100 text-violet-700 px-2.5 py-0.5 rounded-full">
                          {count} variante{count > 1 ? "s" : ""}
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">Sem variantes</span>
                      )}
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-slate-300 group-hover:text-slate-500 shrink-0 mt-1 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ─── Tela de edição ──────────────────────────────────────────────────────────

  const TemplateComponent = TEMPLATES[selectedTemplate.id];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => setSelectedTemplate(null)}
          className="p-2 rounded-lg hover:bg-gray-100 text-slate-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-2xl leading-none">{selectedTemplate.emoji}</span>
        <div>
          <h1 className="text-xl font-semibold text-slate-800">{selectedTemplate.label}</h1>
          <p className="text-sm text-slate-500">{selectedTemplate.desc}</p>
        </div>
      </div>

      <div className="flex flex-col gap-6 items-start lg:flex-row lg:items-stretch lg:h-[calc(100dvh-13rem)] lg:min-h-0">

        {/* Coluna esquerda: controles */}
        <div className="w-full lg:w-80 xl:w-96 shrink-0 lg:min-h-0">
          <div className="space-y-4 lg:h-full lg:overflow-y-auto lg:pr-2">

            {/* Variantes salvas */}
            <div className="bg-white rounded-xl border shadow-sm p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Variantes</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => { setSelectedVariantId(null); setVariantNome(""); setLayoutCfg(defaultLayoutConfig); }}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                    !selectedVariantId
                      ? "border-slate-900 bg-slate-900 text-white font-semibold"
                      : "border-gray-200 text-slate-600 hover:border-gray-400"
                  }`}
                >
                  Padrão do tema
                </button>
                {variants.map(v => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => { setSelectedVariantId(v.id); setVariantNome(v.nome); setLayoutCfg(v.config); }}
                    className={`px-3 py-1.5 text-sm rounded-md border transition-all ${
                      selectedVariantId === v.id
                        ? "border-slate-900 bg-slate-900 text-white font-semibold"
                        : "border-gray-200 text-slate-600 hover:border-gray-400"
                    }`}
                  >
                    {v.nome}
                  </button>
                ))}
              </div>
              {variantsLoading && (
                <p className="text-xs text-slate-400 mt-3">Carregando variantes deste tema...</p>
              )}
            </div>

            {/* Controles de layout */}
            <LayoutControls layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} templateId={selectedTemplate.id} />

            {/* Salvar */}
            <div className="bg-white rounded-xl border shadow-sm p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">
                {selectedVariantId ? "Atualizar variante" : "Salvar como variante"}
              </h3>
              <input
                type="text"
                value={variantNome}
                onChange={e => setVariantNome(e.target.value)}
                placeholder='Ex: "Fundo escuro", "Sem endereço"'
                className="w-full p-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300 mb-3"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-slate-900 text-white text-sm rounded-md hover:bg-slate-800 disabled:opacity-50 font-medium"
                >
                  {saving ? "Salvando..." : selectedVariantId ? "Atualizar" : "Salvar variante"}
                </button>
                {selectedVariantId && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="px-3 py-2 border border-red-200 text-red-600 text-sm rounded-md hover:bg-red-50"
                  >
                    Excluir
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Coluna direita: preview ao vivo */}
        <div className="flex-1 min-w-0 w-full lg:min-h-0">
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden flex flex-col min-h-[720px] lg:min-h-0 lg:h-full">
            <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-slate-700">Pré-visualização</span>
              <div className="flex items-center gap-3">
                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
                  {[
                    { key: "form", label: "Formulário" },
                    { key: "closed", label: "Prazo encerrado" },
                  ].map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setRsvpPreviewMode(option.key)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        rsvpPreviewMode === option.key
                          ? "bg-slate-900 text-white"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <span className="text-xs text-slate-400">Sem interação</span>
              </div>
            </div>
            <div className="flex-1 min-h-0 bg-gray-50">
              <div style={{ overflow: "auto", height: "100%" }}>
              <div style={{
                transform: "scale(0.7)",
                transformOrigin: "top center",
                width: "100%",
                pointerEvents: "none",
                userSelect: "none",
                }}>
                  {TemplateComponent && (
                    <TemplateComponent evento={MOCK_EVENTO} layoutConfig={layoutCfg} templateId={selectedTemplate.id}>
                      <MockRsvpForm
                        templateId={selectedTemplate.id}
                        layoutConfig={layoutCfg}
                        previewMode={rsvpPreviewMode}
                        closedDeadline={MOCK_EVENTO.dataLimiteConfirmacao}
                      />
                    </TemplateComponent>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// ─── Controles de layout ──────────────────────────────────────────────────────

function LayoutControls({ layoutCfg, setLayoutCfg, templateId }) {
  const setCoverImage = (key, val) =>
    setLayoutCfg(c => ({ ...c, coverImage: { ...c.coverImage, [key]: val } }));

  const setBg = (key, val) =>
    setLayoutCfg(c => ({ ...c, background: { ...c.background, [key]: val } }));

  const setSolid = (key, val) =>
    setLayoutCfg(c => ({ ...c, background: { ...c.background, solid: { ...c.background.solid, [key]: val } } }));

  const setGradient = (key, val) =>
    setLayoutCfg(c => ({ ...c, background: { ...c.background, gradient: { ...c.background.gradient, [key]: val } } }));

  const setStop = (idx, key, val) =>
    setLayoutCfg(c => {
      const stops = [...(c.background.gradient?.stops ?? [])];
      stops[idx] = { ...stops[idx], [key]: val };
      return { ...c, background: { ...c.background, gradient: { ...c.background.gradient, stops } } };
    });

  const addStop = () =>
    setLayoutCfg(c => {
      const stops = [...(c.background.gradient?.stops ?? [])];
      if (stops.length >= 4) return c;
      const sorted = [...stops].sort((a, b) => a.position - b.position);
      // Insere no maior intervalo
      let maxGap = -1, insertPos = 50;
      for (let i = 0; i < sorted.length - 1; i++) {
        const gap = sorted[i + 1].position - sorted[i].position;
        if (gap > maxGap) { maxGap = gap; insertPos = Math.round((sorted[i].position + sorted[i + 1].position) / 2); }
      }
      return { ...c, background: { ...c.background, gradient: { ...c.background.gradient, stops: [...stops, { color: "#aaaaaa", position: insertPos }] } } };
    });

  const removeStop = (idx) =>
    setLayoutCfg(c => {
      const stops = c.background.gradient?.stops?.filter((_, i) => i !== idx) ?? [];
      return { ...c, background: { ...c.background, gradient: { ...c.background.gradient, stops } } };
    });

  const bg = layoutCfg.background;
  const ci = layoutCfg.coverImage;
  const sec = layoutCfg.sections;

  const card = "rounded-xl border border-slate-200 bg-slate-50/80 p-4";
  const lbl = "block text-xs font-medium text-gray-600 mb-2";
  const btn = "px-3 py-1.5 text-sm rounded-md border transition-all";
  const on = "border-slate-900 bg-slate-900 text-white font-semibold";
  const off = "border-gray-200 text-slate-500 hover:border-gray-400";

  return (
    <>
      <ControlGroup
        title="Cabeçalho"
        description="Ajuste as cores do topo do convite e o fundo geral da página."
        defaultOpen
      >
        <ColorSection title="Faixa principal" card={card} lbl={lbl}
          bgKey="headerBg" textKey="headerTitleColor"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <div className={card}>
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Fundo da página</h3>
          <div className="flex flex-wrap gap-2 mb-4">
            {[["default", "Padrão"], ["solid", "Sólido"], ["gradient", "Gradiente"]].map(([key, label]) => (
              <button key={key} type="button" onClick={() => setBg("type", key)}
                className={`${btn} ${bg.type === key ? on : off}`}>{label}</button>
            ))}
          </div>

          {bg.type === "solid" && (
            <div className="flex items-end gap-4">
              <div>
                <span className={lbl}>Cor</span>
                <input type="color" value={bg.solid?.color ?? "#ffffff"}
                  onChange={e => setSolid("color", e.target.value)}
                  className="w-12 h-10 rounded border cursor-pointer block" />
              </div>
              <div className="flex-1">
                <span className={lbl}>Opacidade: {bg.solid?.opacity ?? 100}%</span>
                <input type="range" min={10} max={100} value={bg.solid?.opacity ?? 100}
                  onChange={e => setSolid("opacity", parseInt(e.target.value))}
                  className="w-full accent-slate-900" />
              </div>
            </div>
          )}

          {bg.type === "gradient" && (
            <div className="space-y-3">
              <div>
                <span className={lbl}>Direção</span>
                <select value={bg.gradient?.direction ?? "to bottom"}
                  onChange={e => setGradient("direction", e.target.value)}
                  className="w-full p-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-300">
                  <option value="to bottom">Para baixo ↓</option>
                  <option value="to top">Para cima ↑</option>
                  <option value="to right">Para a direita →</option>
                  <option value="to left">Para a esquerda ←</option>
                  <option value="to bottom right">Diagonal ↘</option>
                  <option value="to bottom left">Diagonal ↙</option>
                  <option value="135deg">135°</option>
                </select>
              </div>
              <div className="space-y-2">
                {(bg.gradient?.stops ?? []).map((stop, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input type="color" value={stop.color}
                      onChange={e => setStop(idx, "color", e.target.value)}
                      className="w-9 h-8 rounded border cursor-pointer shrink-0" />
                    <div className="flex-1">
                      <input type="range" min={0} max={100} value={stop.position}
                        onChange={e => setStop(idx, "position", parseInt(e.target.value))}
                        className="w-full accent-slate-900" />
                    </div>
                    <span className="text-xs text-slate-500 w-7 text-right">{stop.position}%</span>
                    {(bg.gradient?.stops?.length ?? 0) > 2 && (
                      <button type="button" onClick={() => removeStop(idx)}
                        className="text-red-400 hover:text-red-600 text-xl leading-none w-5">×</button>
                    )}
                  </div>
                ))}
              </div>
              {(bg.gradient?.stops?.length ?? 0) < 4 && (
                <button type="button" onClick={addStop}
                  className="text-sm text-slate-600 border rounded-md px-3 py-1.5 hover:bg-gray-50">
                  + Adicionar cor
                </button>
              )}
              <div className="h-8 rounded-lg border" style={{
                background: (bg.gradient?.stops?.length ?? 0) >= 2
                  ? `linear-gradient(${bg.gradient.direction}, ${bg.gradient.stops.map(s => `${s.color} ${s.position}%`).join(", ")})`
                  : "#f0f0f0"
              }} />
            </div>
          )}
        </div>
      </ControlGroup>

      <ControlGroup
        title="Foto de Capa"
        description="Defina a apresentação da imagem principal e a faixa ilustrada quando o tema suportar."
      >
        <div className={card}>
          <h3 className="text-sm font-semibold text-slate-700 mb-4">Imagem principal</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className={lbl}>Formato</span>
              <div className="flex flex-wrap gap-2">
                {[["circle", "⬤ Círculo"], ["square", "⬛ Quadrado"]].map(([s, label]) => (
                  <button key={s} type="button" onClick={() => setCoverImage("shape", s)}
                    className={`${btn} ${ci.shape === s ? on : off}`}>{label}</button>
                ))}
              </div>
            </div>
            <div>
              <span className={lbl}>Tamanho</span>
              <div className="flex flex-wrap gap-2">
                {["P", "M", "G"].map(sz => (
                  <button key={sz} type="button" onClick={() => setCoverImage("size", sz)}
                    className={`${btn} ${ci.size === sz ? on : off}`}>{sz}</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {["monica", "stitch", "frozen", "churrasco"].includes(templateId) && (
          <div className={card}>
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Faixa dos personagens</h3>
            <ImageSourceField
              value={layoutCfg.bannerUrl || ""}
              onChange={(bannerUrl) => setLayoutCfg(c => ({ ...c, bannerUrl: bannerUrl || null }))}
              categoria="layout-faixas"
              placeholder="Cole uma URL ou suba uma imagem"
              helpText="Deixe vazio para usar a faixa padrão do tema."
              removeText="Usar imagem padrão"
            />
          </div>
        )}
      </ControlGroup>

      <ControlGroup
        title="Data e Hora"
        description="Personalize o bloco da data e a divisória central entre os elementos."
      >
        <ColorSection title="Bloco da data e hora" card={card} lbl={lbl}
          bgKey="dateBg" textKey="dateTextColor"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Divisória" card={card} lbl={lbl}
          colorKey="dateDividerColor" colorLabel="Cor da divisória"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />
      </ControlGroup>

      <ControlGroup
        title="Endereço"
        description="Controle a aparência da seção de localização, do pin e da forma de exibição."
      >
        <ColorSection title="Bloco do endereço" card={card} lbl={lbl}
          bgKey="addrBg" textKey="addrTextColor"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Divisória" card={card} lbl={lbl}
          colorKey="addrDividerColor" colorLabel="Cor da divisória"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <ChoiceSection
          title="Exibição do endereço"
          card={card}
          value={layoutCfg.addrDisplayMode}
          defaultValue={null}
          onChange={(value) => setLayoutCfg(c => ({ ...c, addrDisplayMode: value }))}
          options={[
            { value: "text", label: "Escrita" },
            { value: "pin", label: "Pin" },
            { value: "pinText", label: "Pin + escrita" },
          ]}
        />

        <SingleColorSection title="Botão do pin" card={card} lbl={lbl}
          colorKey="addrPinBg" colorLabel="Fundo do pin"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <ChoiceSection
          title="Borda do pin"
          card={card}
          value={layoutCfg.addrPinBorderMode}
          defaultValue={null}
          onChange={(value) => setLayoutCfg(c => ({ ...c, addrPinBorderMode: value }))}
          options={[
            { value: "none", label: "Sem borda" },
            { value: "custom", label: "Personalizada" },
          ]}
        />

        <SingleColorSection title="Cor da borda do pin" card={card} lbl={lbl}
          colorKey="addrPinBorderColor" colorLabel="Cor da borda"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <ChoiceSection
          title="Alinhamento do endereço"
          card={card}
          value={layoutCfg.addrAlign}
          defaultValue={null}
          onChange={(value) => setLayoutCfg(c => ({ ...c, addrAlign: value }))}
          options={[
            { value: "center", label: "Centro" },
            { value: "right", label: "Direita" },
            { value: "justify", label: "Justificado" },
          ]}
          defaultLabel="Padrão (esquerda)"
        />

        <div className={card}>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Visibilidade da seção</h3>
          <div className="space-y-2.5">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox"
                checked={(sec.howToGet ?? true) && (sec.address ?? true)}
                onChange={e => setLayoutCfg(c => ({ ...c, sections: { ...c.sections, howToGet: e.target.checked, address: e.target.checked } }))}
                className="w-4 h-4 accent-slate-900" />
              <span className="text-sm text-slate-700">Endereço / Como chegar</span>
            </label>
          </div>
        </div>
      </ControlGroup>

      <ControlGroup
        title="Formulário de Confirmação"
        description="Organize o visual do RSVP, incluindo seções internas, campos e botões."
      >
        <ColorSection title="Base do formulário" card={card} lbl={lbl}
          bgKey="rsvpBg" textKey="rsvpTextColor"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SubgroupLabel
          title="Confirmar Pessoas"
          description="Estilize a área de convidados separadamente do bloco de recado."
        />

        <ColorSection title="Bloco confirmar pessoas" card={card} lbl={lbl}
          bgKey="rsvpPeopleBg" textKey="rsvpPeopleTextColor"
          bgLabel="Fundo do bloco" textLabel="Texto e labels"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Título confirmar pessoas" card={card} lbl={lbl}
          colorKey="rsvpPeopleTitleColor" colorLabel="Cor do título"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SelectSection
          title="Fonte confirmar pessoas"
          card={card}
          label={lbl}
          value={layoutCfg.rsvpPeopleFontFamily}
          onChange={(value) => setLayoutCfg(c => ({ ...c, rsvpPeopleFontFamily: value }))}
          options={RSVP_FONT_OPTIONS}
          defaultLabel="Padrão do tema"
        />

        <ColorSection title="Campos confirmar pessoas" card={card} lbl={lbl}
          bgKey="rsvpPeopleInputBg" textKey="rsvpPeopleInputTextColor"
          bgLabel="Fundo do campo" textLabel="Texto digitado"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Placeholder confirmar pessoas" card={card} lbl={lbl}
          colorKey="rsvpPeopleInputPlaceholderColor" colorLabel="Cor do placeholder"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <ColorSection title="Botão adicionar" card={card} lbl={lbl}
          bgKey="rsvpAddButtonBg" textKey="rsvpAddButtonTextColor"
          bgLabel="Fundo do botão" textLabel="Texto do botão"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SubgroupLabel
          title="Recado"
          description="Defina cores e fonte próprias para a mensagem opcional."
        />

        <ColorSection title="Bloco do recado" card={card} lbl={lbl}
          bgKey="rsvpMessageBg" textKey="rsvpMessageTextColor"
          bgLabel="Fundo do bloco" textLabel="Texto do bloco"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Título do recado" card={card} lbl={lbl}
          colorKey="rsvpMessageTitleColor" colorLabel="Cor do título"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SelectSection
          title="Fonte do recado"
          card={card}
          label={lbl}
          value={layoutCfg.rsvpMessageFontFamily}
          onChange={(value) => setLayoutCfg(c => ({ ...c, rsvpMessageFontFamily: value }))}
          options={RSVP_FONT_OPTIONS}
          defaultLabel="Padrão do tema"
        />

        <ColorSection title="Campo do recado" card={card} lbl={lbl}
          bgKey="rsvpMessageInputBg" textKey="rsvpMessageInputTextColor"
          bgLabel="Fundo do campo" textLabel="Texto digitado"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Placeholder do recado" card={card} lbl={lbl}
          colorKey="rsvpMessageInputPlaceholderColor" colorLabel="Cor do placeholder"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <ColorSection title="Botão confirmar" card={card} lbl={lbl}
          bgKey="rsvpSubmitButtonBg" textKey="rsvpSubmitButtonTextColor"
          bgLabel="Fundo do botão" textLabel="Texto do botão"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />
      </ControlGroup>

      <ControlGroup
        title="Prazo Encerrado"
        description="Personalize a mensagem exibida quando o período de confirmação já terminou."
      >
        <ColorSection title="Bloco do aviso" card={card} lbl={lbl}
          bgKey="rsvpClosedBg" textKey="rsvpClosedTextColor"
          bgLabel="Cor da seção" textLabel="Cor do texto do prazo"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <SingleColorSection title="Título do aviso" card={card} lbl={lbl}
          colorKey="rsvpClosedTitleColor" colorLabel="Cor do título"
          layoutCfg={layoutCfg} setLayoutCfg={setLayoutCfg} />

        <ChoiceSection
          title="Peso do título"
          card={card}
          value={layoutCfg.rsvpClosedTitleWeight}
          defaultValue={null}
          onChange={(value) => setLayoutCfg(c => ({ ...c, rsvpClosedTitleWeight: value }))}
          options={[
            { value: "normal", label: "Normal" },
            { value: "bold", label: "Bold" },
          ]}
          defaultLabel="Padrão do tema"
        />
      </ControlGroup>
    </>
  );
}

function ControlGroup({ title, description, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <section className="bg-white rounded-2xl border shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="w-full px-4 py-4 bg-slate-50/80 hover:bg-slate-100/80 transition-colors text-left"
        aria-expanded={isOpen}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 mb-1">Grupo</p>
            <h3 className="text-base font-semibold text-slate-800">{title}</h3>
            {description && (
              <p className="text-sm text-slate-500 mt-1">{description}</p>
            )}
          </div>
          <span
            className={`mt-1 shrink-0 rounded-full border border-slate-200 bg-white p-2 text-slate-500 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        </div>
      </button>
      {isOpen && (
        <div className="p-4 space-y-3 border-t border-slate-200">
          {children}
        </div>
      )}
    </section>
  );
}

function SubgroupLabel({ title, description }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      {description && (
        <p className="text-xs text-slate-500 mt-1">{description}</p>
      )}
    </div>
  );
}

// ─── Color section reutilizável ──────────────────────────────────────────────

function ColorSection({
  title,
  card,
  lbl,
  bgKey,
  textKey,
  layoutCfg,
  setLayoutCfg,
  bgLabel = "Cor de fundo",
  textLabel = "Cor do texto",
}) {
  const bgVal   = layoutCfg[bgKey];
  const textVal = layoutCfg[textKey];
  const setVal  = (key, val) => setLayoutCfg(c => ({ ...c, [key]: val }));

  return (
    <div className={card}>
      <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <span className={lbl}>{bgLabel}</span>
          <div className="flex items-center gap-2">
            <input type="color" value={bgVal ?? "#ffffff"}
              onChange={e => setVal(bgKey, e.target.value)}
              className="w-12 h-10 rounded border cursor-pointer block" />
            {bgVal && (
              <button type="button" onClick={() => setVal(bgKey, null)}
                className="text-xs text-slate-500 hover:text-slate-700">↺ padrão</button>
            )}
          </div>
        </div>
        <div>
          <span className={lbl}>{textLabel}</span>
          <div className="flex items-center gap-2">
            <input type="color" value={textVal ?? "#000000"}
              onChange={e => setVal(textKey, e.target.value)}
              className="w-12 h-10 rounded border cursor-pointer block" />
            {textVal && (
              <button type="button" onClick={() => setVal(textKey, null)}
                className="text-xs text-slate-500 hover:text-slate-700">↺ padrão</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SingleColorSection({ title, card, lbl, colorKey, colorLabel, layoutCfg, setLayoutCfg }) {
  const value = layoutCfg[colorKey];
  const setVal = (key, val) => setLayoutCfg(c => ({ ...c, [key]: val }));

  return (
    <div className={card}>
      <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      <div>
        <span className={lbl}>{colorLabel}</span>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={value ?? "#9ca3af"}
            onChange={e => setVal(colorKey, e.target.value)}
            className="w-12 h-10 rounded border cursor-pointer block"
          />
          {value && (
            <button
              type="button"
              onClick={() => setVal(colorKey, null)}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              ↺ padrão
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ChoiceSection({ title, card, value, defaultValue = null, defaultLabel = "Padrão", onChange, options }) {
  const btn = "px-3 py-1.5 text-sm rounded-md border transition-all";
  const on = "border-slate-900 bg-slate-900 text-white font-semibold";
  const off = "border-gray-200 text-slate-500 hover:border-gray-400";

  return (
    <div className={card}>
      <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onChange(defaultValue)}
          className={`${btn} ${value === null || value === undefined ? on : off}`}
        >
          {defaultLabel}
        </button>
        {options.map(option => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`${btn} ${value === option.value ? on : off}`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function SelectSection({ title, card, label, value, onChange, options, defaultLabel = "Padrão" }) {
  return (
    <div className={card}>
      <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      <div>
        <span className={label}>Fonte</span>
        <select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value || null)}
          className="w-full p-2.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 bg-white"
        >
          <option value="">{defaultLabel}</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
